import { $userLanguage } from '@/entities/user'
import { $steamUserData } from '@/entities/platforms/steam/store'
import { $farcanaStatistics } from '@/entities/integration/model/store'
import { lang } from '@/shared/constants/lang'
import { FormattedStat } from '@/shared/constants/types'
import { differenceInCalendarDays } from 'date-fns'
import { useUnit } from 'effector-react'
import { useCallback, useEffect, useState } from 'react'
import { $epicUserData } from '@/entities/platforms/egs'

function useFormatUnifiedStats() {
  const userLang = useUnit($userLanguage)

  const units = lang[userLang].pages.units
  const texts = lang[userLang].pages.profile.unifiedStats
  const steamUserData = useUnit($steamUserData)
  const farcanaStats = useUnit($farcanaStatistics)
  const epicUserData = useUnit($epicUserData)

  const calculateDailyPlaytime = useCallback((): number => {
    if (!steamUserData?.userData.registredAt || !steamUserData.userData.totalHoursPlayed) return 0
    const accountAgeInDays = differenceInCalendarDays(Date.now(), new Date(steamUserData.userData.registredAt))
    const FAR_avgPlaytime = farcanaStats?.totalHoursPlayed ?? 0
    const STEAM_avgPlaytime = steamUserData.userData.totalHoursPlayed / accountAgeInDays
    return Number(STEAM_avgPlaytime.toFixed(2)) + FAR_avgPlaytime
  }, [steamUserData?.userData.registredAt, steamUserData?.userData.totalHoursPlayed, farcanaStats?.totalHoursPlayed])

  const [stats, setStats] = useState<FormattedStat[]>([])

  useEffect(() => {
    const STEAM_totalHoursPlayed = steamUserData?.userData.totalHoursPlayed ?? 0
    const FAR_totalHoursPlayed = farcanaStats?.totalHoursPlayed ?? 0
    const TOTAL_playtime = STEAM_totalHoursPlayed + FAR_totalHoursPlayed
    const TOTAL_gamesOwned = steamUserData?.userData.gamesOwned ?? 0
    const STEAM_itemsOwned = steamUserData?.userData.itemsOwned ?? 0
    const STEAM_itemsPrice = steamUserData?.userData.inventoryPrice ?? 0
    const FAR_itemsPrice = farcanaStats?.totalAssetsValue ?? 0

    const TOTAL_itemsPrice = Math.floor(Number(STEAM_itemsPrice + FAR_itemsPrice))

    const STEAM_accValue = steamUserData?.userData.totalValue ?? 0
    const EGS_accValue = epicUserData?.total_value_usd ?? 0
    const FAR_accValue = farcanaStats?.totalAssetsValue ?? 0
    const TOTAL_accValue = STEAM_accValue + FAR_accValue + EGS_accValue

    setStats([
      { name: texts.totalAccVal, value: TOTAL_accValue, unit: units.dollar },
      { name: texts.totalAssPur, value: TOTAL_itemsPrice, unit: units.dollar },
      { name: texts.totalHrPlayed, value: TOTAL_playtime, unit: units.hours },
      { name: texts.dailyPlaytime, value: calculateDailyPlaytime(), unit: units.hours },
      { name: texts.gamesOwned, value: TOTAL_gamesOwned },
      { name: texts.itemsOwned, value: STEAM_itemsOwned },
    ])
  }, [
    userLang,
    steamUserData,
    farcanaStats,
    epicUserData,
    texts.totalAccVal,
    texts.totalAssPur,
    texts.totalHrPlayed,
    texts.dailyPlaytime,
    texts.gamesOwned,
    texts.itemsOwned,
    calculateDailyPlaytime,
    units.dollar,
    units.hours,
  ])

  return stats
}

export default useFormatUnifiedStats
